import { isInboxClinicalTask } from '@bariendo/lib/inbox-tasks';
import { PostHog } from 'posthog-js';
import { NotificationItem } from '@/components/NotificationTable/types';

/**
 * Utility functions for PostHog analytics tracking
 * These functions provide type-safe wrappers for PostHog events defined in BAR-1360
 */

/**
 * Safe wrapper for PostHog capture that prevents analytics errors from halting execution
 */
function safeCapture(posthog: PostHog, eventName: string, params: Record<string, unknown>): void {
  try {
    posthog.capture(eventName, params);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(`Failed to track "${eventName}":`, error);
  }
}

// ===== SEARCH & DISCOVERY EVENTS =====

export function trackSearchPerformed(
  posthog: PostHog,
  params: {
    search_type: 'patient' | 'patient timeline' | 'partner';
    source: 'global header' | 'patient list' | 'patient timeline' | 'partner list';
    query_length?: number;
    results_count?: number;
    selected_result_rank?: number;
  }
): void {
  safeCapture(posthog, 'search performed', params);
}

export function trackFilterApplied(
  posthog: PostHog,
  params: {
    page: 'inbox' | 'archive' | 'patient list' | 'partner list' | 'patient timeline';
    filter_types: string[];
    results_count?: number;
    patient_id?: string;
  }
): void {
  safeCapture(posthog, 'filter applied', params);
}

// ===== NAVIGATION CONTEXT EVENTS =====

export function trackInboxItemOpened(
  posthog: PostHog,
  params: {
    source: 'inbox row' | 'push notification' | 'archive row';
    inbox_item_type: 'call' | 'sms' | 'chat' | 'note' | 'task';
    is_read?: boolean;
    time_since_created_seconds?: number;
    patient_id: string;
  }
): void {
  safeCapture(posthog, 'inbox item opened', params);
}

export function trackCallInitiated(
  posthog: PostHog,
  params: {
    source: 'inbox row' | 'patient timeline header' | 'partner timeline header' | 'global phone controls';
    patient_id?: string;
    organization_id?: string;
    inbox_item_task_id?: string;
  }
): void {
  safeCapture(posthog, 'call initiated', params);
}

// ===== CALL RECORDING PLAYBACK EVENTS =====

export function trackCallRecordingPlayed(
  posthog: PostHog,
  params: {
    call_id: string;
    playback_speed: number;
    playhead_position_seconds?: number;
    patient_id?: string;
    organization_id?: string;
  }
): void {
  safeCapture(posthog, 'call recording played', params);
}

export function trackCallRecordingPaused(
  posthog: PostHog,
  params: {
    call_id: string;
    playback_speed: number;
    playhead_position_seconds?: number;
    patient_id?: string;
    organization_id?: string;
  }
): void {
  safeCapture(posthog, 'call recording paused', params);
}

// ===== DOCUMENT INTERACTION EVENTS =====

export function trackDocumentViewed(
  posthog: PostHog,
  params: {
    document_type: string;
    source: 'patient timeline' | 'documents pane';
    patient_id?: string;
    organization_id?: string;
  }
): void {
  safeCapture(posthog, 'document viewed', params);
}

export function trackDocumentDownloaded(
  posthog: PostHog,
  params: {
    document_type: string;
    source: 'patient timeline' | 'documents pane';
    patient_id?: string;
    organization_id?: string;
  }
): void {
  safeCapture(posthog, 'document downloaded', params);
}

export function trackEncounterPDFDownloaded(
  posthog: PostHog,
  params: {
    encounter_id: string;
    patient_id?: string;
    tasks_count?: number;
  }
): void {
  safeCapture(posthog, 'encounter pdf downloaded', params);
}

// ===== ABANDONED ACTIONS EVENTS =====

export function trackMessageDraftAbandoned(
  posthog: PostHog,
  params: {
    message_type: 'chat' | 'sms' | 'note';
    draft_length?: number;
    had_attachments?: boolean;
    had_mentions?: boolean;
    time_spent_seconds?: number;
    patient_id?: string;
    organization_id?: string;
  }
): void {
  safeCapture(posthog, 'message draft abandoned', params);
}

// ===== UI ACTIONS EVENTS =====

export function trackDirectLinkCopied(
  posthog: PostHog,
  params: {
    item_type: 'call' | 'sms' | 'chat' | 'note' | 'fax' | 'unknown';
    source: 'kebab menu' | 'context menu';
    patient_id: string;
  }
): void {
  safeCapture(posthog, 'direct link copied', params);
}

export function trackInboxMonitoringStarted(
  posthog: PostHog,
  params: {
    monitored_practitioner_id: string;
    view_type: 'inbox' | 'archive';
  }
): void {
  safeCapture(posthog, 'inbox monitoring started', params);
}

export function trackInboxMonitoringStopped(
  posthog: PostHog,
  params: {
    monitored_practitioner_id: string;
    view_type: 'inbox' | 'archive';
  }
): void {
  safeCapture(posthog, 'inbox monitoring stopped', params);
}

export function trackSidebarToggled(
  posthog: PostHog,
  params: {
    new_state: 'collapsed' | 'expanded';
  }
): void {
  safeCapture(posthog, 'sidebar toggled', params);
}

// ===== UTILITY FUNCTIONS =====

/**
 * Helper to determine inbox item type from a NotificationItem
 */
export function getInboxItemType(notification: NotificationItem): 'call' | 'sms' | 'chat' | 'note' | 'task' {
  if (isInboxClinicalTask(notification.task, notification.sourceCommunication)) {
    return 'task';
  }

  const category = notification.sourceCommunication?.category?.[0]?.coding?.[0]?.code;

  if (category === 'phone-call' || category === 'partner-call') return 'call';
  if (category === 'sms-message') return 'sms';
  if (category === 'chat' || category === 'thread') return 'chat';
  // timeline-note, standalone-note, or any other category
  return 'note';
}

/**
 * Calculate time since creation in seconds
 */
export function getTimeSinceCreatedSeconds(createdAt: string | Date): number {
  const created = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  const now = new Date();
  return Math.floor((now.getTime() - created.getTime()) / 1000);
}
